import { CommonModule } from '@angular/common';
import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, OnDestroy, OnInit, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { addDays, addHours, endOfDay, formatISO, isAfter, isBefore, parseISO, startOfDay, subHours } from 'date-fns';
import { Subject } from 'rxjs';
import { first, map, takeUntil } from 'rxjs/operators';
import { Pop } from 'src/app/classes/shared/pop';
import { DashboardManagerComponent } from 'src/app/components/project-pages/dashboard/dialogs/dashboard-manager/dashboard-manager.component';
import { openWidgetCatalog } from 'src/app/components/project-pages/dashboard/dialogs/widget-catalog/widget-catalog.component';
import { Project } from 'src/app/interfaces/api/project';
import { Unit } from 'src/app/interfaces/api/unit';
import { User } from 'src/app/interfaces/api/user';
import { Dashboard } from 'src/app/interfaces/shared/dashboard';
import { DashboardService } from 'src/app/services/api/dashboards/dashboard.service';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { UnitService } from 'src/app/services/api/units/unit.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { ThemeService } from 'src/app/services/core/theme/theme.service';
import { DashboardStore } from 'src/app/services/stores/dashboard.store';
import { ActionGroupComponent, ButtonComponent, DatePickerComponent, RangePickerComponent, SpinnerComponent } from 'ui-library';
import { NoDataComponent } from "../../../../../projects/ui-library/src/lib/components/no-data/no-data.component";
import { ShareReportDialogComponent } from '../../shared/dialogs/share-report/share-report-dialog.component';
import { DashboardPanelComponent } from './dashboard-panel/dashboard-panel.component';
import { ReportListDialogComponent } from './dialogs/report-list-dialog/report-list-dialog.component';

@Component({
  selector: 'app-dashboard',
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatSidenavModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTooltipModule,
    MatDividerModule,
    MatSlideToggleModule,
    MatRippleModule,
    TranslateModule,
    DatePickerComponent,
    RangePickerComponent,
    ActionGroupComponent,
    DashboardPanelComponent,
    SpinnerComponent,
    NoDataComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  standalone: true,
})

export class DashboardComponent implements OnInit, OnDestroy {
  // Services
  private readonly metricService: MetricService = inject(MetricService);
  public readonly translate: TranslateService = inject(TranslateService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly auth: AuthService = inject(AuthService);
  private readonly dashboardService: DashboardService = inject(DashboardService);
  private readonly pop: Pop = inject(Pop);
  private readonly unitService: UnitService = inject(UnitService);
  private readonly themeService: ThemeService = inject(ThemeService);
  public readonly dashboardStore: DashboardStore = inject(DashboardStore);

  // Component API
  @ViewChild('filterDrawer') filterDrawer: any;

  // Variables
  loading: Boolean = false;
  playMode: string = 'realtime';
  startDate: string = formatISO(startOfDay(new Date()));
  endDate: string = formatISO(endOfDay(new Date()));
  metricsOnly = true;
  showEndDate = false;
  currentProject: any;
  showFilters = false;
  showFieldList = false;
  createdDateFilter = {
    name: 'createdDate',
    type: 'Date',
    enable: false,
    options: {
      dateType: 'Range',
      periodAmount: 1,
      periodValue: 'year',
      useMax: false,
      max: this.endDate,
      eqMax: false,
      useMin: true,
      min: this.startDate,
      eqMin: false,
    },
  };
  filters: any[] = [];
  metricFields: any = {};
  valueList: any[] | undefined;
  currentField: any;
  replayProgress: boolean = false;
  loadingFields: boolean = false;
  types = {
    ObjectID: 'title', //play_arrow
    String: 'title',
    Number: 'format_list_numbered',
    Boolean: 'tonality', //tonality, brightness_6
    Mixed: 'arrow_right', //play_arrow
    Date: 'date_range',
    Array: 'arrow_right',
  };
  backgroundColor: string = '#eee';
  borderColor: string = '#bbb';
  progressTextColor = '#1b1b1b';

  // Test chart properties
  testChart: any = {
    id: 'test-chart-001',
    title: 'Test Block Chart',
    type: 'block',
    showTitle: true,
    titlePosition: 'top',
    visible: true,
    content: 'Hello Dashboard! This is a test text component.',
    query: null,
    design: {
      fontSize: '24px',
      fontColor: '#333333',
      backgroundColor: '#f8f9fa'
    }
  };

  selectedProjects: string[] = [];
  helpLink: string = 'https://cad42.atlassian.net/wiki/x/BwD8IQ';
  destroy$ = new Subject<void>();
  units: Unit[] = [];


  private lastProjectId: string | undefined;
  private pendingDashboardId: string | undefined;
  private _canNavigateNext: boolean = true;
  private lastTheme: string = '';

  private readonly visitedDashboards = new Map<string, string>();

  // Signals
  readonly userSignal: Signal<User> = this.auth.currentUser;
  readonly currentProjectSignal: Signal<Project> = this.projectService.currentProjectSignal;
  readonly isLoadingWidgets: Signal<boolean> = this.dashboardStore.isLoading;
  readonly activitySignal: WritableSignal<any> = signal(null);
  readonly visibleDashboardIds: WritableSignal<string[]> = signal<string[]>([]);
  // ?fit=true: dashboard fitted to the screen (big screens).
  readonly fit: Signal<boolean> = toSignal(this.route.queryParamMap.pipe(map(params => params.get('fit') === 'true')), { initialValue: false });

  dashboardsList!: Signal<Dashboard[]>;
  refreshSignal: WritableSignal<number> = signal(0);
  exportSignal: WritableSignal<string> = signal('');
  loadingDashboards: WritableSignal<boolean> = signal(false);

  // Computeds
  readonly isLoading = computed(() => {
    return this.loadingDashboards() || this.isLoadingWidgets();
  });

  get currentDashboard(): Dashboard | undefined {
    return this.dashboardStore.currentDashboard();
  }

  get canNavigateNext(): boolean {
    return this._canNavigateNext;
  }

  constructor() {
    const queryParamsSignal = toSignal(this.route.queryParamMap);

    if (this.userSignal()) {
      this.backgroundColor = 'var(--background-color)';
      this.borderColor = 'var(--content-background-color)';
      this.progressTextColor = 'var(--text-color)';
      this.filters = [this.createdDateFilter];
    }

    this.dashboardsList = this.dashboardStore.sortedDashboards;
    const initialDashboardId = this.route.snapshot.queryParamMap.get('dashboardId');
    let initialProjectId = this.route.snapshot.queryParamMap.get('project');

    if (initialDashboardId) {
      this.pendingDashboardId = initialDashboardId;
    }

    // 🎯 Effect currentProject
    effect(() => {
      const project = this.currentProjectSignal();
      if (!project?.id) return;

      const isProjectChange = this.lastProjectId && this.lastProjectId !== project.id;
      const isInitialLoad = !this.lastProjectId;

      this.currentProject = project;
      this.selectedProjects = [project.id];
      this.lastProjectId = project.id;

      if (initialProjectId && project.id != initialProjectId) {
        this.projectService.getProjectById(initialProjectId)
          .pipe(first())
          .subscribe((updatedProject) => {
            initialProjectId = null;
            this.projectService.setCurrentProject(updatedProject);
          });
      } else {
        this.dashboardStore.setCurrentProjectId(project.id);

        if (isProjectChange) {
          this.visitedDashboards.clear();
          this.visibleDashboardIds.set([]);
          this.loadDashboards(this.pendingDashboardId ?? null);
          this.pendingDashboardId = undefined;
        } else if (isInitialLoad) {
          this.loadDashboards(this.pendingDashboardId ?? null);
          this.pendingDashboardId = undefined;
        }
      }

      this.projectService.getActivity({ databases: [project.database] }).pipe(takeUntil(this.destroy$)).subscribe((activity: any) => {
        this.activitySignal.set(activity);
      });
    });

    // 🔗 Effect changement params url
    effect(() => {
      const params = queryParamsSignal();
      if (!params) return;

      const dashboardId = params.get('dashboardId');
      const mode = params.get('mode');
      const metricsOnly = params.get('metricsOnly');
      const startDate = params.get('startDate');
      const endDate = params.get('endDate');

      let datesChanged = false;

      if (startDate) {
        this.startDate = startDate;
      }

      if (endDate) {
        this.endDate = endDate;
        this.showEndDate = true;
        datesChanged = true;
      } else if (startDate) {
        this.endDate = formatISO(endOfDay(parseISO(startDate)));
        datesChanged = true;
      }

      if (mode) {
        this.setPlayMode(mode);
      }

      this.metricsOnly = metricsOnly !== 'false';
      this.getDashboard(dashboardId)

      if (datesChanged) {
        this.updateDateFilter();
      }
    });

    if (isAfter(parseISO(this.startDate), parseISO(this.endDate))) {
      this.startDate = formatISO(subHours(parseISO(this.endDate), 7));
    }

    this.updateDateFilter();

    // 🔄 Effect multi-theme
    effect(() => {
      const theme = this.themeService.theme();

      if (!this.lastTheme) {
        this.lastTheme = theme;
        return;
      }

      if (theme !== this.lastTheme) {
        this.lastTheme = theme;
        this.refreshAllWidgets();
      }
    });

    // 🗂️ Effect pool de dashboards visités
    effect(() => {
      const currentId = this.dashboardStore.currentDashboardId();

      if (currentId && !this.visitedDashboards.has(currentId)) {
        this.visitedDashboards.set(currentId, currentId);
        const ids = Array.from(this.visitedDashboards.keys());
        this.visibleDashboardIds.set(ids);
      }
    });
  }

  /////////////////////////////
  // Angular lifecycle hooks //
  /////////////////////////////

  ngOnInit() {
    this.unitService.getUnits().pipe(takeUntil(this.destroy$)).subscribe((res) => {
      this.units = res;
    });
  }

  refreshAllWidgets() {
    this.refreshSignal.set(this.refreshSignal() + 1);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getDashboard(dashboardId: string | null) {
    const dashboards = this.dashboardsList();

    if (dashboardId && dashboardId !== this.dashboardStore.currentDashboardId() && dashboards.length) {
      const dashboard = dashboards.find(d => d.id === dashboardId);

      if (dashboard) {
        this.dashboardStore.setCurrentDashboardId(dashboardId);
      }
    } else if (dashboardId && !dashboards.length) {
      if (!this.pendingDashboardId) {
        this.pendingDashboardId = dashboardId;
      }
    } else if (!dashboardId && dashboards.length > 0 && dashboards[0]) {
      this.dashboardStore.setCurrentDashboardId(dashboards[0].id);
    }
  }

  selectField(field: { name: string; type: any; }, option: { name: string; type: any; } | null) {
    this.showFieldList = false;
    let currentField: { name: string; type: any; options: any };
    if (option) {
      currentField = {
        name: field.name + '.' + option.name,
        type: option.type,
        options: {}
      };
    } else {
      currentField = {
        name: field.name,
        type: field.type,
        options: {}
      };
    }

    if (currentField.type == 'String') {
      currentField.options['selectAll'] = false;
      currentField.options['valueList'] = [];
      currentField.options['value'] = [];

      this.metricService
        .getValueList(currentField.name)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res: any) => {
          currentField.options['valueList'] = res.map((r: { _id: any; }) => r._id);
        });
    } else if (currentField.type == 'Number') {
      currentField.options = {
        useMax: false,
        max: 0,
        eqMax: false,
        useMin: false,
        min: 0,
        eqMin: false,
      };
    } else if (currentField.type == 'Date') {
      currentField.options = {
        dateType: 'Range',
        periodRolling: false,
        periodAmount: 1,
        periodValue: 'year',
        max: '',
        eqMax: false,
        min: '',
        eqMin: false,
      };
    }

    this.filters.push({ ...currentField });
  }

  setFieldValueSelection(event: any, filter: { options: { selectAll: boolean; value: any[]; }; }) {
    const checked = event?.checked ?? false;

    if (checked) {
      filter.options.selectAll = true;
      filter.options.value = [...filter.options.value];
    } else {
      filter.options.selectAll = false;
      filter.options.value = [];
    }
  }

  //----------------Date fonction ----------------//

  onDateChanged(event: any, type?: string, updatePicker?: any) {
    if (typeof event === 'boolean') {
      return;
    }

    let dateFilter = this.filters.find((f) => f.name == 'createdDate');

    if (!dateFilter) return;

    if (type == 'startDate' && event?.value) {
      dateFilter.options.min = formatISO(event.value);
    } else if (type == 'endDate' && event?.value) {
      dateFilter.options.max = formatISO(event.value);
    }

    let options = dateFilter.options;
    let startDate, endDate;

    if (options.dateType == 'Period') {
      const now = new Date();

      let startMoment = new Date(now);

      if (options.periodValue === 'year') {
        startMoment.setFullYear(now.getFullYear() - options.periodAmount);
      } else if (options.periodValue === 'month') {
        startMoment.setMonth(now.getMonth() - options.periodAmount);
      } else if (options.periodValue === 'day') {
        startMoment = addDays(now, -options.periodAmount);
      }

      if (options.periodRolling) {
        startMoment = startOfDay(startMoment);
      }

      startDate = formatISO(startMoment);
      endDate = formatISO(now);
      this.showEndDate = true;
    } else {
      if (options.useMin) {
        startDate = formatISO(parseISO(options.min));
      }
      if (options.useMax) {
        endDate = formatISO(parseISO(options.max));
      }

      this.showEndDate = options.useMax;
    }

    if (updatePicker)
      this.onPickerChanged({ startDate, endDate });
  }

  onPickerChanged(event: any, isRange: boolean = false): void {
    if (!event) return;

    if (this.playMode === 'realtime') {
      this.setPlayMode('replay');
    }

    if (isRange) {
      this.startDate = parseISO(event.startDate).toISOString();
      this.endDate = parseISO(event.endDate).toISOString();
    } else {
      const date = event.startDate || event;
      const parsedDate = parseISO(date);
      this.startDate = startOfDay(parsedDate).toISOString();
      this.endDate = endOfDay(parsedDate).toISOString();
    }

    this.updateDateFilter();
    this.navigateToDate();
  }


  onRangePickerChanged(event: any) {
    if (!event) return;

    if (this.playMode === 'realtime')
      this.setPlayMode('replay');

    const startDate = parseISO(event.startDate);
    const endDate = parseISO(event.endDate);

    this.startDate = startDate.toISOString();
    this.endDate = endDate.toISOString();

    this.updateDateFilter();
    this.navigateDate(0);
  }

  onShowEndChange(): void {
    this.showEndDate = !this.showEndDate;

    if (!this.startDate || !this.endDate) return;

    if (!this.showEndDate) {
      const baseDate = parseISO(this.startDate);
      this.startDate = startOfDay(baseDate).toISOString();
      this.endDate = endOfDay(baseDate).toISOString();
    }

    this.updateDateFilter();
    this.navigateToDate();
  }

  navigateDate(offset: number): void {
    if (this.playMode === 'realtime') {
      this.setPlayMode('replay');
    }

    const currentDate = new Date();
    const newStartDate = addDays(parseISO(this.startDate), offset);
    const newEndDate = addDays(parseISO(this.endDate), offset);

    if (isBefore(newStartDate, currentDate) || isBefore(newEndDate, currentDate)) {
      this.startDate = newStartDate.toISOString();
      this.endDate = newEndDate.toISOString();
      this.updateDateFilter();
      this.navigateToDate();
    }
  }

  private navigateToDate(): void {
    const params: Params = {
      mode: this.playMode,
      metricsOnly: this.metricsOnly,
      startDate: this.startDate,
      dashboardId: this.dashboardStore.currentDashboardId(),
      endDate: this.showEndDate ? this.endDate : null,
    };

    this.router.navigate([], {
      queryParams: params,
      queryParamsHandling: 'merge',
    });

    this.refreshAllWidgets();
    this.verifCanNavigateNext();
  }

  verifCanNavigateNext(): void {
    const today = startOfDay(new Date());
    const currentDisplayedDate = this.showEndDate
      ? parseISO(this.endDate)
      : parseISO(this.startDate);
    const nextDate = startOfDay(addDays(currentDisplayedDate, 1));

    this._canNavigateNext =
      isBefore(nextDate, today) || nextDate.getTime() === today.getTime();
  }

  setPlayMode(mode: string): void {
    this.playMode = mode;

    if (mode === 'realtime') {
      const now = new Date();
      this.startDate = formatISO(addHours(startOfDay(now), 7));
      this.endDate = formatISO(now);

      if (isAfter(parseISO(this.startDate), parseISO(this.endDate))) {
        this.startDate = formatISO(subHours(parseISO(this.endDate), 7));
      }

      this.updateDateFilter();

      const queryParams: Params = {
        mode: 'realtime',
        metricsOnly: this.metricsOnly,
        play: true,
        startDate: this.startDate,
        endDate: this.showEndDate ? this.endDate : undefined,
      };

      this.router.navigate([], {
        queryParams,
        queryParamsHandling: 'merge',
      });
    }

    this.verifCanNavigateNext();
  }

  updateDateFilter(): void {
    const dateFilter = this.filters.find((f) => f.name === 'createdDate');

    if (dateFilter?.options.dateType === 'Range') {
      dateFilter.options.min = this.startDate;
      dateFilter.options.useMin = true;
      dateFilter.options.eqMin = false;
      dateFilter.options.useMax = this.showEndDate;
      dateFilter.options.max = this.endDate;
      dateFilter.options.eqMax = false;
      dateFilter.enable = this.playMode === 'replay';
    }
  }

  //-------------------------------------------//

  refresh() {
    this.refreshSignal.set(this.refreshSignal() + 1);
  }

  // Full screen left to the user (F11, kiosk): the fit follows the window.
  toggleFit(): void {
    this.router.navigate([], { queryParams: { fit: this.fit() ? null : true }, queryParamsHandling: 'merge' });
  }

  shareExport() {
    this.exportSignal.set(`canvas_${(new Date()).getTime()}`);
  }

  shareReport() {
    const currentProject = this.userSignal().currentProject;
    const projectKey = currentProject && typeof currentProject === 'object' && 'flowAccessKey' in currentProject ? currentProject.flowAccessKey ?? '' : '';

    this.dialog.open(ShareReportDialogComponent, {
      width: '420px',
      data: {
        title: this.translate.instant('workflows.reportUrl'),
        url: `${window.location.origin}/report?project=${currentProject}
        &key=${projectKey}
        &startDate=${this.startDate}
        &endDate=${this.endDate}
        &metricsOnly=${this.metricsOnly}`,
      },
    });
  }

  shareAccess() {
    const currentProject = this.userSignal().currentProject;
    const projectKey = currentProject && typeof currentProject === 'object' && 'flowAccessKey' in currentProject ? currentProject.flowAccessKey ?? '' : '';

    const dialogRef = this.dialog.open(ShareReportDialogComponent, {
      width: '420px',
      data: {
        title: this.translate.instant('dashboard.share.link_title'),
        url: `${window.location.origin}/public?project=${encodeURIComponent(currentProject?.toString() ?? '')}
          &key=${encodeURIComponent(projectKey)}
          &startDate=${encodeURIComponent(this.startDate)}
          &endDate=${encodeURIComponent(this.endDate)}
          &metrics=${encodeURIComponent(this.metricsOnly)}
          &filters=${encodeURIComponent(JSON.stringify(this.filters))}
          &backgroundColor=${encodeURIComponent(this.backgroundColor)}
          &borderColor=${encodeURIComponent(this.borderColor)}
          &progressTextColor=${encodeURIComponent(this.progressTextColor)}`,
        filters: JSON.stringify(this.filters),
        public: true,
        icon: 'public',
      },
    });

    dialogRef.afterClosed().subscribe((res) => { });
  }

  mailReports(userOnly: boolean = false) {
    let query = { startDate: this.startDate, endDate: this.endDate, userOnly };

    this.dashboardService.mailReports(query).subscribe({
      next: (data: any) => {
        this.pop.toast(this.translate.instant(data.message), 'success', 8000, 'email');
      },
      error: (err) => {
        this.pop.error(this.translate.instant(err.error.message));
      }
    });
  }

  showReports() {
    const dialogRef = this.dialog.open(ReportListDialogComponent, {
      width: '700px',
      height: '600px',
      data: {
        title: this.translate.instant('dashboard.reports_list'),
      },
    });

    dialogRef.afterClosed().subscribe((res) => { });
  }

  // New widget: placed at the bottom by the grid.
  addWidgetEditor(item = null) {
    openWidgetCatalog(this.dialog, {
      title: this.translate.instant('editor.header'),
      item,
      dashboard: this.dashboardStore.currentDashboard()
    }).subscribe((data) => {
      if (data?.dashboard) {
        this.invalidateCache();
        this.refreshAllWidgets();
      }
    });
  }

  private loadDashboards(targetDashboardId: string | null): void {
    if (!this.currentProject) return;

    this.loadingDashboards.set(true);

    this.dashboardService.getDashboards({ projects: this.currentProject.id }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        const dashboards = this.dashboardsList();
        this.selectDashboard(targetDashboardId, dashboards);
        this.loadingDashboards.set(false);
      },
      error: (error) => {
        console.error('❌ Error loading dashboards:', error);
        this.loadingDashboards.set(false);
        this.refreshSignal.set(Date.now());
      }
    });
  }

  private selectDashboard(targetId: string | null, dashboards: Dashboard[]): void {
    const selected = this.determineSelectedDashboard(dashboards, targetId ?? undefined);

    if (selected) {
      this.dashboardStore.setCurrentDashboardId(selected.id);
      this.navigateDashboard(selected);
    } else {
      this.dashboardStore.setCurrentDashboardId(null);
    }
  }

  private determineSelectedDashboard(dashboards: Dashboard[], targetDashboardId?: string): Dashboard | undefined {
    // 1️⃣ Priority to the dashboard specified in the URL (targetDashboardId)
    if (targetDashboardId) {
      const targetDashboard = dashboards.find(d => d.id === targetDashboardId);

      if (targetDashboard)
        return targetDashboard;
    }

    // 2️⃣ Then try to keep the currently selected dashboard if it still exists
    const currentId = this.dashboardStore.currentDashboardId();
    if (currentId) {
      const currentStillExists = dashboards.find(d => d.id === currentId);
      if (currentStillExists) return currentStillExists;
    }

    return dashboards.length > 0 ? dashboards[0] : undefined;
  }

  private navigateDashboard(dashboard: Dashboard | undefined): void {
    const currentDashboardId = this.route.snapshot.queryParamMap.get('dashboardId');
    if (!dashboard) {

      if (currentDashboardId) {
        this.router.navigate([], {
          queryParams: { dashboardId: null, project: null },
          queryParamsHandling: 'merge',
        });
      }

      return;
    }

    if (dashboard.id !== currentDashboardId) {
      this.router.navigate([], {
        queryParams: { dashboardId: dashboard.id, project: null },
        queryParamsHandling: 'merge',
      });
    }
  }

  addDashboard() {
    const editModal = this.dialog.open(DashboardManagerComponent, {
      width: '900px',
      height: '620px',
      data: {
        title: this.translate.instant('workflows.edition_dashboard'),
        currentDashboardId: null
      },
    });

    editModal.afterClosed().subscribe((res) => {
      if (res?.updated) {
        console.log('✅ Dashboard updated, store already synced');
      }
    });
  }

  invalidateCache() {
    const currentDashboardId = this.dashboardStore.currentDashboardId();

    if (currentDashboardId) {
      this.dashboardStore.invalidateWidgetsCacheForDashboard(currentDashboardId);
    }
  }
}
